import { DETipoConstanciaAsociadoEnum, DETipoDocumentoAsociadoEnum } from "@/shared/types/documento-electronico";
import { z } from "zod/v4";

// Schema reutilizable para pagos
export const pagoSchema = z.object({
    tipoPago: z.enum(["efectivo", "tarjeta_credito", "tarjeta_debito", "cheque", "transferencia"]),
    monto: z.number().min(0, "Monto debe ser mayor o igual a 0"),
    tipoTarjeta: z.string().optional(),
    otroTipoTarjeta: z.string().optional(),
    procesadorPago: z.string().optional(),
    numeroCheque: z.string().optional(),
    bancoEmisor: z.string().optional()
});

//Base schema de todos los DE
export const baseDESchema = z.object({
    clienteId: z.string().default(""),
    monedaOperacionId: z.string().optional(),
    conversionGuarani: z.number().optional(),
    tipoTransaccion: z.enum(["1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12", "13"]).default("2"), // iTipTra - Default: Prestación de servicios
    condicionOperacion: z.enum(["contado", "credito"]).default("contado"), // iCondOpe

    // Campos para contado - múltiples tipos de pago
    pagos: z.array(pagoSchema).optional().default([]),

    // Campos para crédito
    tipoCredito: z.enum(["plazo", "cuotas"]).optional(),
    plazoDias: z.string().optional(),
    numeroCuotas: z.number().optional(),

    observaciones: z.string().optional(),
    items: z.array(z.object({
        codigo: z.string().min(1, "Código es requerido"),
        descripcion: z.string().min(1, "Descripción es requerida"),
        cantidad: z.number().min(1, "Cantidad debe ser mayor a 0"),
        precio: z.number().min(0, "Precio debe ser mayor o igual a 0"),
        iva: z.enum(["exenta", "5", "10"]).default("10"),
        unidadMedida: z.number().min(1, "Unidad de medida es requerida"),
        dDncpG: z.string().optional(), // Obligatorio si B2G
        dDncpE: z.string().optional(), // Obligatorio si B2G
        cdcAnticipo: z.string().optional(), // Obligatorio si el Emisor tiene Tipo de Transacción = Anticipo
    })).min(1, "Debe agregar al menos un item")
});

//Schema for documento asociado (autofactura/ nota crédito)
export const documentoAsociadoSchema = z.object({
    tipoDocumentoAsociado: z.enum(["1", "2", "3"]).default("1"),
    cdcDocumentoAsociado: z.string().optional(),
    timbradoDocImpAsociado: z.string().default("").refine(
        (val) => !val || val.trim() === "" || /^\d{8}$/.test(val),
        { message: "Debe tener 8 dígitos" }
    ),
    establecimientoDocImpAsociado: z.string().default("").refine(
        (val) => !val || val.trim() === "" || /^\d{3}$/.test(val),
        { message: "Debe tener 3 dígitos" }
    ),
    puntoExpedicionDocImpAsociado: z.string().default("").refine(
        (val) => !val || val.trim() === "" || /^\d{3}$/.test(val),
        { message: "Debe tener 3 dígitos" }
    ),
    numeroDocImpAsociado: z.string().default("").refine(
        (val) => !val || val.trim() === "" || /^\d{7}$/.test(val),
        { message: "Debe tener 7 dígitos" }
    ),
    tipoComprobanteImpreso: z.number().default(1),
    fechaEmisionDocImpAsociado: z.string().or(z.date()).nullable().optional(),
    // Constancia Electronica
    tipoConstancia: z.enum(["1", "2"]).default("1"), // iTipCons: 1=Nota de Remisión, 2=Comprobante de Retención
    numeroConstancia: z.string().default("").refine(
        (val) => !val || val.trim() === "" || /^\d{11}$/.test(val),
        { message: "Debe tener 11 dígitos" }
    ),
    numeroControl: z.string().default("").refine(
        (val) => !val || val.trim() === "" || /^[a-zA-Z0-9]{8}$/.test(val),
        { message: "Debe tener 8 caracteres" }
    ),
    // Obligatorio en Autofactura cuando el Emisor tiene Tipo de Transacción = Venta de crédito fiscal
    // (validado en addDocumentoAsociadoValidation, no acá, para no perder el valor tipeado si falla el largo)
    numeroResolucionCF: z.string().optional(),
});

//Validación para condición de operación OBLIGATORIA (para factura y autofactura)
export function addPaymentValidationWithCondicionRequired<T extends z.ZodTypeAny>(schema: T) {
    return schema.superRefine((data: any, ctx) => {

        // Validar que condicionOperacion sea requerido
        if (!data.condicionOperacion) {
            ctx.addIssue({ code: "custom", message: "Condición de operación es requerida", path: ["condicionOperacion"] });
            return;
        }

        if (data.condicionOperacion === "contado") {
            // Calcular total de items
            let totalItems = 0;
            if (data.items && data.items.length > 0) {
                data.items.forEach((item: any) => {
                    const subtotal = (Number(item.cantidad) || 0) * (Number(item.precio) || 0);
                    totalItems += subtotal;
                });
            }

            // Calcular total de pagos (0 si no hay pagos aún)
            const pagosArr = Array.isArray(data.pagos) ? data.pagos : [];
            const totalPagos = pagosArr.reduce((acc: number, p: any) => acc + (Number(p.monto) || 0), 0);

            // Validar que los totales coincidan (margen 1 por redondeo)
            if (Math.abs(Math.round(totalItems) - Math.round(totalPagos)) != 0) {
                const lastPagoIndex = pagosArr.length > 0 ? pagosArr.length - 1 : -1;

                if (lastPagoIndex >= 0) {
                    ctx.addIssue({
                        code: "custom",
                        message: `(${Math.round(totalPagos).toLocaleString()}) no coincide con el total de la operación (${Math.round(totalItems).toLocaleString()})`,
                        path: [`pagos.${lastPagoIndex}.monto`]
                    });
                } else {
                    // Si no hay pagos cargados aún, mostrar el error a nivel del array
                    ctx.addIssue({
                        code: "custom",
                        message: `(${Math.round(totalPagos).toLocaleString()}) no coincide con el total de la operación (${Math.round(totalItems).toLocaleString()})`,
                        path: ["errorOperacionPago"]
                    });
                }
            }

            // Validar cada pago solo si existen pagos
            if (data.pagos && data.pagos.length > 0) {
                data.pagos.forEach((pago: any, index: number) => {
                    // Validar monto > 0 solo si se agregó un pago
                    /*if (pago.monto <= 0)
                        ctx.addIssue({ code: "custom", message: "Monto debe ser mayor a 0", path: [`pagos.${index}.monto`] });*/

                    // Validar campos de tarjeta
                    if (pago.tipoPago === "tarjeta_credito" || pago.tipoPago === "tarjeta_debito") {
                        if (!pago.tipoTarjeta)
                            ctx.addIssue({ code: "custom", message: "Tipo de tarjeta es requerido", path: [`pagos.${index}.tipoTarjeta`] });

                        if (pago.tipoTarjeta && pago.tipoTarjeta == "99") {
                            if (!pago.otroTipoTarjeta || pago.otroTipoTarjeta.trim() === "")
                                ctx.addIssue({ code: "custom", message: "Denominación de tarjeta es requerida", path: [`pagos.${index}.otroTipoTarjeta`] });
                            else if (pago.otroTipoTarjeta.trim().length < 4 || pago.otroTipoTarjeta.trim().length > 20)
                                ctx.addIssue({ code: "custom", message: "Denominación de tarjeta debe tener entre 4 y 20 caracteres", path: [`pagos.${index}.otroTipoTarjeta`] });
                        }

                        if (!pago.procesadorPago)
                            ctx.addIssue({ code: "custom", message: "Procesador de pago es requerido", path: [`pagos.${index}.procesadorPago`] });
                    }

                    // Validar campos de cheque
                    if (pago.tipoPago === "cheque") {
                        if (!pago.numeroCheque)
                            ctx.addIssue({ code: "custom", message: "Número de cheque es requerido", path: [`pagos.${index}.numeroCheque`] });
                        else if (pago.numeroCheque.length != 8)
                            ctx.addIssue({ code: "custom", message: "Número de cheque debe tener 8 dígitos", path: [`pagos.${index}.numeroCheque`] });

                        if (!pago.bancoEmisor)
                            ctx.addIssue({ code: "custom", message: "Banco emisor es requerido", path: [`pagos.${index}.bancoEmisor`] });
                        else if (pago.bancoEmisor.length < 4 || pago.bancoEmisor.length > 20)
                            ctx.addIssue({ code: "custom", message: "Banco emisor debe tener entre 4 y 30 caracteres", path: [`pagos.${index}.bancoEmisor`] });
                    }
                });
            }
        } else if (data.condicionOperacion === "credito") {
            // Validar crédito
            if (data.tipoCredito === "plazo") {
                if (!data.plazoDias)
                    ctx.addIssue({ code: "custom", message: "Plazo es requerido", path: ["plazoDias"] });

            } else if (data.tipoCredito === "cuotas") {
                if (!data.numeroCuotas || data.numeroCuotas < 1)
                    ctx.addIssue({ code: "custom", message: "Número de cuotas es requerido", path: ["numeroCuotas"] });
            }
        }
    });
}

const TIPO_TRANSACCION_VENTA_CREDITO_FISCAL = "12";

//Validacion para documentos asociados
// optional=true: permite no informar documento asociado (valor "none" o vacío)
// requiereNumResCFSiCreditoFiscal=true: exige numeroResolucionCF cuando el Emisor tiene Tipo de Transacción = Venta de crédito fiscal
export function addDocumentoAsociadoValidation<T extends z.ZodTypeAny>(schema: T, optional = false, requiereNumResCFSiCreditoFiscal = false) {
    return schema.superRefine((data: any, ctx) => {
        const tipo = data.tipoDocumentoAsociado;
        if (optional && (!tipo || tipo === "none")) return;

        const esCreditoFiscalObligatorio = requiereNumResCFSiCreditoFiscal && data.tipoTransaccion === TIPO_TRANSACCION_VENTA_CREDITO_FISCAL;
        if (esCreditoFiscalObligatorio && !data.numeroResolucionCF) {
            ctx.addIssue({ code: "custom", message: "N° de Resolución de Crédito Fiscal es requerido", path: ["numeroResolucionCF"] });
        } else if (data.numeroResolucionCF && data.numeroResolucionCF.length !== 15) {
            ctx.addIssue({ code: "custom", message: "Debe tener 15 caracteres", path: ["numeroResolucionCF"] });
        }

        if (tipo === DETipoDocumentoAsociadoEnum.Electronico) {
            if (!data.cdcDocumentoAsociado)
                ctx.addIssue({ code: "custom", message: "CDC es requerido para documento electrónico", path: ["cdcDocumentoAsociado"] });

        } else if (tipo === DETipoDocumentoAsociadoEnum.Impreso) {
            const timbrado = data.timbradoDocImpAsociado;
            if (!timbrado || timbrado.trim() === "")
                ctx.addIssue({ code: "custom", message: "Timbrado es requerido", path: ["timbradoDocImpAsociado"] });

            const establecimiento = data.establecimientoDocImpAsociado;
            if (!establecimiento || establecimiento.trim() === "")
                ctx.addIssue({ code: "custom", message: "Establecimiento es requerido", path: ["establecimientoDocImpAsociado"] });

            const puntoExpedicion = data.puntoExpedicionDocImpAsociado;
            if (!puntoExpedicion || puntoExpedicion.trim() === "")
                ctx.addIssue({ code: "custom", message: "Punto de expedición es requerido", path: ["puntoExpedicionDocImpAsociado"] });

            const numero = data.numeroDocImpAsociado;
            if (!numero || numero.trim() === "")
                ctx.addIssue({ code: "custom", message: "Número es requerido", path: ["numeroDocImpAsociado"] });

            if (!data.tipoComprobanteImpreso)
                ctx.addIssue({ code: "custom", message: "Tipo de comprobante es requerido", path: ["tipoComprobanteImpreso"] });

            if (!data.fechaEmisionDocImpAsociado)
                ctx.addIssue({ code: "custom", message: "Fecha de emisión es requerida", path: ["fechaEmisionDocImpAsociado"] });

        } else if (tipo === DETipoDocumentoAsociadoEnum.ConstanciaElectronica) {
            if (!data.tipoConstancia)
                ctx.addIssue({ code: "custom", message: "Tipo de constancia es requerido", path: ["tipoConstancia"] });

            if (data.tipoConstancia === DETipoConstanciaAsociadoEnum.DeMicroproductores) {
                if (!data.numeroConstancia)
                    ctx.addIssue({ code: "custom", message: "Número de constancia es requerido", path: ["numeroConstancia"] });

                if (!data.numeroControl)
                    ctx.addIssue({ code: "custom", message: "Número de control es requerido", path: ["numeroControl"] });
            }
        }
    });
}
